import React, { useEffect, useState, useCallback } from 'react';
import { Plus, Pencil, Trash2, Loader2, Eye, EyeOff, Archive, CheckCircle2, X, Save } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { useToast } from '@/hooks/use-toast';
import { AdminAPI } from '../../lib/api';
import ImageUploader from '../../components/admin/ImageUploader';

const STATUS_STYLES = {
  published: 'bg-green-100 text-green-700',
  draft: 'bg-slate-100 text-slate-600',
  unpublished: 'bg-amber-100 text-amber-700',
  archived: 'bg-red-100 text-red-700',
};

const EMPTY = {
  code: '', name: '', shortDescription: '', brand: '', category: '',
  price: '', priceLabel: '', onRequest: false, image: '',
  accuracy: '', aperture: '', measurementSpeed: '', color: '#0A2A63',
  featuresText: '', specsText: '', status: 'draft', order: 0,
};

const toForm = (p) => ({
  code: p.code || '', name: p.name || '', shortDescription: p.shortDescription || '',
  brand: p.brand || '', category: p.category || '',
  price: p.price != null ? String(p.price) : '',
  priceLabel: p.priceLabel || '',
  onRequest: !!p.priceLabel && p.price == null,
  image: p.image || '',
  accuracy: p.accuracy || '', aperture: p.aperture || '', measurementSpeed: p.measurementSpeed || '',
  color: p.color || '#0A2A63',
  featuresText: (p.features || []).join('\n'),
  specsText: Object.entries(p.specs || {}).map(([k, v]) => `${k}: ${v}`).join('\n'),
  status: p.status || 'draft', order: p.order || 0,
});

const toPayload = (f) => {
  const specs = {};
  f.specsText.split('\n').map((l) => l.trim()).filter(Boolean).forEach((line) => {
    const i = line.indexOf(':');
    if (i > 0) specs[line.slice(0, i).trim()] = line.slice(i + 1).trim();
  });
  return {
    code: f.code.trim(), name: f.name.trim(), shortDescription: f.shortDescription.trim(),
    brand: f.brand.trim(), category: f.category.trim(),
    price: f.onRequest ? null : (f.price === '' ? null : Number(f.price)),
    priceLabel: f.onRequest ? (f.priceLabel.trim() || 'On request') : null,
    image: f.image.trim(),
    accuracy: f.accuracy.trim(), aperture: f.aperture.trim(), measurementSpeed: f.measurementSpeed.trim(),
    color: f.color, features: f.featuresText.split('\n').map((s) => s.trim()).filter(Boolean),
    specs, status: f.status, order: Number(f.order) || 0,
  };
};

const ProductForm = ({ open, onClose, editing, onSaved }) => {
  const { toast } = useToast();
  const [f, setF] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setF((prev) => ({ ...prev, [k]: v }));

  useEffect(() => { setF(editing ? toForm(editing) : EMPTY); }, [editing, open]);

  const submit = async (e) => {
    e.preventDefault();
    if (!f.code.trim() || !f.name.trim()) {
      toast({ title: 'Missing fields', description: 'Code and name are required.' });
      return;
    }
    setSaving(true);
    try {
      const payload = toPayload(f);
      if (editing) await AdminAPI.updateProduct(editing.id, payload);
      else await AdminAPI.createProduct(payload);
      toast({ title: 'Saved', description: `“${payload.name}” ${editing ? 'updated' : 'created'} · status: ${payload.status}` });
      onSaved();
      onClose();
    } catch (err) {
      toast({ title: 'Error', description: err?.response?.data?.detail || 'Could not save.' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="product-form-dialog">
        <DialogHeader>
          <DialogTitle className="text-2xl font-black text-[#050F2E]">{editing ? 'Edit product' : 'New product'}</DialogTitle>
          <DialogDescription>Changes apply across the Shop, product pages, home carousel and comparison table.</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="grid gap-4 mt-2">
          <div className="grid grid-cols-2 gap-3">
            <div><Label className="text-xs font-bold uppercase tracking-widest text-slate-500">Code *</Label>
              <Input value={f.code} onChange={(e) => set('code', e.target.value)} className="mt-1.5 h-11 rounded-xl" data-testid="pf-code" /></div>
            <div><Label className="text-xs font-bold uppercase tracking-widest text-slate-500">Name *</Label>
              <Input value={f.name} onChange={(e) => set('name', e.target.value)} className="mt-1.5 h-11 rounded-xl" data-testid="pf-name" /></div>
            <div><Label className="text-xs font-bold uppercase tracking-widest text-slate-500">Brand</Label>
              <Input value={f.brand} onChange={(e) => set('brand', e.target.value)} className="mt-1.5 h-11 rounded-xl" data-testid="pf-brand" /></div>
            <div><Label className="text-xs font-bold uppercase tracking-widest text-slate-500">Category</Label>
              <Input value={f.category} onChange={(e) => set('category', e.target.value)} className="mt-1.5 h-11 rounded-xl" data-testid="pf-category" /></div>
          </div>

          <div><Label className="text-xs font-bold uppercase tracking-widest text-slate-500">Short description</Label>
            <Textarea value={f.shortDescription} onChange={(e) => set('shortDescription', e.target.value)} rows={2} className="mt-1.5 rounded-xl resize-none" data-testid="pf-desc" /></div>

          <ImageUploader label="Product image" value={f.image} onChange={(v) => set('image', v)} testId="pf-image" />

          {/* Pricing */}
          <div className="grid grid-cols-3 gap-3 items-end">
            <label className="flex items-center gap-2 text-sm font-semibold text-slate-700">
              <input type="checkbox" checked={f.onRequest} onChange={(e) => set('onRequest', e.target.checked)} data-testid="pf-onrequest" /> On request
            </label>
            {f.onRequest ? (
              <div className="col-span-2"><Label className="text-xs font-bold uppercase tracking-widest text-slate-500">Price label</Label>
                <Input value={f.priceLabel} onChange={(e) => set('priceLabel', e.target.value)} placeholder="On request" className="mt-1.5 h-11 rounded-xl" data-testid="pf-pricelabel" /></div>
            ) : (
              <div className="col-span-2"><Label className="text-xs font-bold uppercase tracking-widest text-slate-500">Price (USD)</Label>
                <Input type="number" value={f.price} onChange={(e) => set('price', e.target.value)} className="mt-1.5 h-11 rounded-xl" data-testid="pf-price" /></div>
            )}
          </div>

          <div className="grid grid-cols-4 gap-3">
            <div><Label className="text-xs font-bold uppercase tracking-widest text-slate-500">Accuracy</Label>
              <Input value={f.accuracy} onChange={(e) => set('accuracy', e.target.value)} className="mt-1.5 h-11 rounded-xl" /></div>
            <div><Label className="text-xs font-bold uppercase tracking-widest text-slate-500">Aperture</Label>
              <Input value={f.aperture} onChange={(e) => set('aperture', e.target.value)} className="mt-1.5 h-11 rounded-xl" /></div>
            <div><Label className="text-xs font-bold uppercase tracking-widest text-slate-500">Speed</Label>
              <Input value={f.measurementSpeed} onChange={(e) => set('measurementSpeed', e.target.value)} className="mt-1.5 h-11 rounded-xl" /></div>
            <div><Label className="text-xs font-bold uppercase tracking-widest text-slate-500">Accent</Label>
              <input type="color" value={f.color} onChange={(e) => set('color', e.target.value)} className="mt-1.5 h-11 w-full rounded-xl border border-slate-200" /></div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div><Label className="text-xs font-bold uppercase tracking-widest text-slate-500">Features (one per line)</Label>
              <Textarea value={f.featuresText} onChange={(e) => set('featuresText', e.target.value)} rows={5} className="mt-1.5 rounded-xl resize-none font-mono text-xs" data-testid="pf-features" /></div>
            <div><Label className="text-xs font-bold uppercase tracking-widest text-slate-500">Specs (Key: Value per line)</Label>
              <Textarea value={f.specsText} onChange={(e) => set('specsText', e.target.value)} rows={5} className="mt-1.5 rounded-xl resize-none font-mono text-xs" data-testid="pf-specs" /></div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div><Label className="text-xs font-bold uppercase tracking-widest text-slate-500">Status</Label>
              <select value={f.status} onChange={(e) => set('status', e.target.value)} className="mt-1.5 h-11 w-full rounded-xl border border-slate-200 px-3 text-sm font-semibold" data-testid="pf-status">
                <option value="draft">Draft</option>
                <option value="published">Published</option>
                <option value="unpublished">Unpublished</option>
                <option value="archived">Archived</option>
              </select></div>
            <div><Label className="text-xs font-bold uppercase tracking-widest text-slate-500">Order</Label>
              <Input type="number" value={f.order} onChange={(e) => set('order', e.target.value)} className="mt-1.5 h-11 rounded-xl" /></div>
          </div>

          <div className="flex justify-end gap-3 pt-2">
            <Button type="button" variant="outline" onClick={onClose} className="rounded-full h-11 px-6 font-bold">Cancel</Button>
            <Button type="submit" disabled={saving} className="bg-[#0A2A63] hover:bg-[#061a3f] text-white rounded-full h-11 px-6 font-bold" data-testid="pf-submit">
              {saving ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Saving...</> : <><Save className="h-4 w-4 mr-2" /> Save</>}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

const AdminProducts = () => {
  const { toast } = useToast();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try { setItems(await AdminAPI.listProducts()); }
    catch { toast({ title: 'Error', description: 'Could not load products.' }); }
    finally { setLoading(false); }
  }, [toast]);

  useEffect(() => { load(); }, [load]);

  const setStatus = async (id, status) => {
    try { await AdminAPI.setProductStatus(id, status); toast({ title: 'Updated', description: `Status: ${status}` }); load(); }
    catch { toast({ title: 'Error', description: 'Could not update status.' }); }
  };

  const remove = async (id, name) => {
    if (!window.confirm(`Remove “${name}” from the site? This archives it.`)) return;
    try { await AdminAPI.deleteProduct(id); toast({ title: 'Removed', description: name }); load(); }
    catch { toast({ title: 'Error', description: 'Could not delete.' }); }
  };

  return (
    <div data-testid="admin-products-page">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-3xl font-black text-[#050F2E] tracking-tight">Products</h1>
          <p className="text-slate-500 mt-1">{items.length} products · manage the Shop catalog</p>
        </div>
        <Button onClick={() => { setEditing(null); setFormOpen(true); }} className="bg-[#0A2A63] hover:bg-[#061a3f] text-white rounded-full h-11 px-6 font-bold" data-testid="admin-new-product">
          <Plus className="h-4 w-4 mr-2" /> New product
        </Button>
      </div>

      {loading ? (
        <div className="flex items-center gap-2 text-slate-400"><Loader2 className="h-5 w-5 animate-spin" /> Loading...</div>
      ) : (
        <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-500 uppercase text-[10px] tracking-widest">
              <tr>
                <th className="text-left p-4">Product</th>
                <th className="text-left p-4">Brand</th>
                <th className="text-left p-4">Price</th>
                <th className="text-left p-4">Status</th>
                <th className="text-right p-4">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {items.map((p) => (
                <tr key={p.id} className="hover:bg-slate-50" data-testid={`admin-product-row-${p.id}`}>
                  <td className="p-4">
                    <div className="flex items-center gap-3">
                      <div className="h-12 w-12 rounded-lg bg-slate-100 flex items-center justify-center overflow-hidden flex-shrink-0">
                        {p.image ? <img src={p.image} alt={p.name} className="h-full w-full object-contain" /> : null}
                      </div>
                      <div>
                        <p className="font-bold text-[#050F2E] leading-tight">{p.name}</p>
                        <p className="text-xs text-slate-400 font-mono">{p.code}</p>
                      </div>
                    </div>
                  </td>
                  <td className="p-4 text-slate-600">{p.brand}</td>
                  <td className="p-4 font-semibold text-[#0A2A63]">{p.price ? `$${Number(p.price).toLocaleString()}` : (p.priceLabel || '—')}</td>
                  <td className="p-4">
                    <span className={`inline-block px-2.5 py-1 rounded-full text-[10px] font-bold uppercase tracking-wide ${STATUS_STYLES[p.status] || 'bg-slate-100 text-slate-600'}`}>{p.status}</span>
                  </td>
                  <td className="p-4">
                    <div className="flex items-center justify-end gap-1">
                      {p.status !== 'published' ? (
                        <button onClick={() => setStatus(p.id, 'published')} title="Publish" className="h-8 w-8 rounded-lg hover:bg-green-100 text-green-700 flex items-center justify-center" data-testid={`publish-${p.id}`}><CheckCircle2 className="h-4 w-4" /></button>
                      ) : (
                        <button onClick={() => setStatus(p.id, 'unpublished')} title="Unpublish" className="h-8 w-8 rounded-lg hover:bg-amber-100 text-amber-700 flex items-center justify-center" data-testid={`unpublish-${p.id}`}><EyeOff className="h-4 w-4" /></button>
                      )}
                      <button onClick={() => setStatus(p.id, 'archived')} title="Archive" className="h-8 w-8 rounded-lg hover:bg-slate-100 text-slate-500 flex items-center justify-center" data-testid={`archive-${p.id}`}><Archive className="h-4 w-4" /></button>
                      <button onClick={() => { setEditing(p); setFormOpen(true); }} title="Edit" className="h-8 w-8 rounded-lg hover:bg-slate-100 text-[#0A2A63] flex items-center justify-center" data-testid={`edit-${p.id}`}><Pencil className="h-4 w-4" /></button>
                      <button onClick={() => remove(p.id, p.name)} title="Delete" className="h-8 w-8 rounded-lg hover:bg-red-100 text-red-600 flex items-center justify-center" data-testid={`delete-${p.id}`}><Trash2 className="h-4 w-4" /></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <ProductForm open={formOpen} editing={editing} onClose={() => setFormOpen(false)} onSaved={load} />
    </div>
  );
};

export default AdminProducts;
